import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Lock, Minus, Plus } from "lucide-react";
import { colors, type, spacing, radius, formatCents, initials } from "@/theme";
import { api } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";

export default function CustomerConto() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { session } = useCustomer();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coperti, setCoperti] = useState(1);
  const [savingCoperti, setSavingCoperti] = useState(false);

  const load = useCallback(async () => {
    if (!session) { setLoading(false); return; }
    try {
      const res = await api.getSession(session.session_id);
      setData(res);
      setCoperti(res.coperti_dichiarati == null ? (res.commensali?.length || 1) : res.coperti_dichiarati);
    } catch {} finally { setLoading(false); }
  }, [session]);

  useEffect(() => { load(); }, [load]);

  const confirmCoperti = async () => {
    if (!session) return;
    setSavingCoperti(true);
    try { setData(await api.setCoperti(session.session_id, coperti)); } finally { setSavingCoperti(false); }
  };

  if (!session) {
    return (
      <div style={{ minHeight: "100vh", background: colors.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: spacing.md }}>
        <div style={{ ...type.serif, fontSize: 18, color: colors.inkSoft }}>Nessuna sessione attiva</div>
        <button onClick={() => navigate("/", { replace: true })} style={{ background: colors.accent, height: 52, border: "none", borderRadius: radius.md, color: colors.onAccent, ...type.sansSemi, fontSize: 16, padding: `0 ${spacing.xl}px` }}>Torna alla home</button>
      </div>
    );
  }

  const groups = {};
  (data?.righe || []).forEach((r) => { (groups[r.guest_nome] = groups[r.guest_nome] || []).push(r); });
  const coperiConfirmed = data?.coperti_dichiarati != null;
  const showCoperto = data?.mostra_coperto && data?.coperto_cent > 0;

  return (
    <div style={{ minHeight: "100vh", background: colors.bg }}>
      <div style={{ maxWidth: 480, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${spacing.md}px ${spacing.lg}px` }}>
          <button data-testid="back-button" onClick={() => navigate(-1)} style={{ background: "none", border: "none", display: "flex", padding: 0 }}><ChevronLeft size={26} color={colors.ink} /></button>
          <div style={{ ...type.serif, fontSize: 20, color: colors.ink }}>Conto · Tavolo {session.etichetta}</div>
          <div style={{ width: 26 }} />
        </div>

        {loading ? <div style={{ textAlign: "center", padding: spacing.xl, ...type.sans, color: colors.accent }}>Caricamento…</div> : (
          <div style={{ padding: spacing.lg, display: "flex", flexDirection: "column", gap: spacing.md }}>
            {data?.stato === "chiusa" ? (
              <div style={{ display: "flex", alignItems: "center", gap: spacing.sm, background: colors.surfaceTertiary, padding: spacing.md, borderRadius: radius.md }}>
                <Lock size={14} color={colors.inkSoft} />
                <span style={{ ...type.sansMedium, fontSize: 13, color: colors.inkSoft }}>Sessione chiusa. Il menu resta consultabile.</span>
              </div>
            ) : null}

            {Object.keys(groups).length === 0 ? (
              <div style={{ textAlign: "center", padding: `${spacing.xxxl}px 0`, ...type.serif, fontSize: 18, color: colors.inkSoft }}>Ancora nessun piatto ordinato</div>
            ) : Object.entries(groups).map(([nome, rows]) => (
              <div key={nome} style={{ background: colors.surface, borderRadius: radius.lg, padding: spacing.md, border: `1px solid ${colors.line}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: spacing.sm, marginBottom: spacing.sm }}>
                  <div style={{ width: 32, height: 32, borderRadius: 16, background: colors.accentSoft, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ ...type.sansSemi, fontSize: 12, color: colors.accent }}>{initials(nome)}</span></div>
                  <span style={{ ...type.sansSemi, fontSize: 15, color: colors.ink }}>{nome}</span>
                </div>
                {rows.map((r, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", padding: "6px 0", gap: spacing.sm }}>
                    <span style={{ ...type.sansSemi, fontSize: 14, color: colors.inkSoft, minWidth: 26 }}>{r.quantita}×</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ ...type.sans, fontSize: 15, color: colors.ink }}>{r.nome}</div>
                      {(r.note_rapide?.length || r.nota_riga) ? <div style={{ ...type.sans, fontSize: 12, color: colors.accent, marginTop: 2 }}>{[...(r.note_rapide || []), r.nota_riga].filter(Boolean).join(" · ")}</div> : null}
                    </div>
                    <span style={{ ...type.sansMedium, fontSize: 14, color: colors.ink }}>{formatCents(r.prezzo_cent * r.quantita)}</span>
                  </div>
                ))}
              </div>
            ))}

            {showCoperto ? (
              <div style={{ background: colors.accentSoft, borderRadius: radius.lg, padding: spacing.lg, display: "flex", flexDirection: "column", gap: spacing.sm }}>
                <div style={{ ...type.serif, fontSize: 19, color: colors.ink }}>Quante persone siete?</div>
                <div style={{ ...type.sans, fontSize: 13, color: colors.inkSoft, lineHeight: "19px" }}>Serve per il coperto ({formatCents(data.coperto_cent)} a persona). Anche chi non ordina conta.</div>
                <div style={{ display: "flex", alignItems: "center", gap: spacing.md, marginTop: spacing.sm }}>
                  <div style={{ display: "flex", alignItems: "center", background: colors.surface, borderRadius: radius.md, border: `1px solid ${colors.line}` }}>
                    <button data-testid="coperti-minus" onClick={() => setCoperti((c) => Math.max(0, c - 1))} style={{ width: 44, height: 46, background: "none", border: "none", display: "flex", alignItems: "center", justifyContent: "center" }}><Minus size={18} color={colors.ink} /></button>
                    <span style={{ ...type.sansSemi, fontSize: 18, color: colors.ink, minWidth: 30, textAlign: "center" }}>{coperti}</span>
                    <button data-testid="coperti-plus" onClick={() => setCoperti((c) => c + 1)} style={{ width: 44, height: 46, background: "none", border: "none", display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={18} color={colors.ink} /></button>
                  </div>
                  <button data-testid="confirm-coperti" onClick={confirmCoperti} disabled={savingCoperti} style={{ flex: 1, height: 46, background: colors.accent, border: "none", borderRadius: radius.md, color: colors.onAccent, ...type.sansSemi, fontSize: 15 }}>{coperiConfirmed ? "Aggiorna" : "Conferma"}</button>
                </div>
              </div>
            ) : null}

            <div style={{ background: colors.surface, borderRadius: radius.lg, padding: spacing.lg, border: `1px solid ${colors.line}`, display: "flex", flexDirection: "column", gap: spacing.sm }}>
              <div style={totalRow}><span style={{ ...type.sans, fontSize: 15, color: colors.inkSoft }}>Piatti</span><span style={{ ...type.sansMedium, fontSize: 15, color: colors.ink }}>{formatCents(data?.piatti_totale_cent || 0)}</span></div>
              {showCoperto && coperiConfirmed ? <div style={totalRow}><span style={{ ...type.sans, fontSize: 15, color: colors.inkSoft }}>Coperto ({data.coperti_dichiarati} ×)</span><span style={{ ...type.sansMedium, fontSize: 15, color: colors.ink }}>{formatCents(data.coperto_totale_cent)}</span></div> : null}
              <div style={{ height: 1, background: colors.line, margin: `${spacing.xs}px 0` }} />
              <div style={totalRow}><span style={{ ...type.serif, fontSize: 20, color: colors.ink }}>Totale</span><span data-testid="conto-total" style={{ ...type.serif, fontSize: 22, color: colors.accent }}>{formatCents(data?.totale_cent || 0)}</span></div>
            </div>

            <p style={{ ...type.sans, fontSize: 12, color: colors.inkSoft, lineHeight: "18px", textAlign: "center", padding: `0 ${spacing.md}px` }}>Riepilogo indicativo, non è uno scontrino fiscale. Il conto definitivo è quello della cassa.</p>
          </div>
        )}
      </div>
    </div>
  );
}

const totalRow = { display: "flex", justifyContent: "space-between", alignItems: "center" };
